// obsidian-vault.js

import { bilderUmschreiben, vaultHtml } from './vault-bilder.js';
import { listenFaltbarMachen } from './vault-listen-falten.js';
import { startdatei, sichtbareDateien } from './vault-startdatei.js';
import { markierungen, ohneFrontmatter } from './vault-notiz.js';
import { mermaidAuslagern, mermaidRendern, mermaidThemaBeobachten } from './vault-mermaid.js';

/**
 * ObsidianVaultManager
 * Manages the Obsidian Vault interface, file tree, search, and preview
 */
export class ObsidianVaultManager {
    constructor() {
        this.panel = document.getElementById('obsidian-vault-interface');
        this.fileTreeContent = document.getElementById('vault-tree-content');
        this.previewContent = document.getElementById('vault-preview-content');
        this.currentFileSpan = document.getElementById('vault-current-file');
        this.searchInput = document.getElementById('vault-search-input');
        this.vaultPicker = document.getElementById('vault-picker');
        this.vaultPickerMenu = document.getElementById('vault-picker-menu');
        this.vaultConfig = null;

        this.files = [];
        this.currentFile = null;
        this.currentFilePath = null;
        this.isFullscreen = false;

        this.initializeEventListeners();
        mermaidThemaBeobachten(this.previewContent);
    }

    /**
     * Initialize all event listeners
     */
    initializeEventListeners() {
        // Close button
        const closeBtn = document.getElementById('btn-vault-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
        }

        // Toggle sidebar button
        const fullscreenBtn = document.getElementById('btn-vault-fullscreen');
        if (fullscreenBtn) {
            fullscreenBtn.addEventListener('click', () => this.toggleVollbild());
        }

        const toggleSidebarBtn = document.getElementById('btn-vault-toggle-sidebar');
        if (toggleSidebarBtn) {
            toggleSidebarBtn.addEventListener('click', () => this.toggleSidebar());
        }

        // Refresh button
        const refreshBtn = document.getElementById('btn-refresh-vault');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this.loadFiles());
        }

        // Vault-Auswahl: Name oeffnet die Liste, Ordner-Icon den Dialog
        const vaultNameBtn = document.getElementById('btn-vault-name');
        if (vaultNameBtn) {
            vaultNameBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleVaultMenu();
            });
        }

        const browseBtn = document.getElementById('btn-vault-browse');
        if (browseBtn) {
            browseBtn.addEventListener('click', () => this.browseVault());
        }

        // Klick ausserhalb schliesst die Liste
        document.addEventListener('click', (e) => {
            if (this.vaultPicker && !this.vaultPicker.contains(e.target)) {
                this.closeVaultMenu();
            }
        });

        // Toggle search button
        const toggleSearchBtn = document.getElementById('btn-vault-toggle-search');
        const searchContainer = document.querySelector('.vault-search-container');
        const searchBar = document.getElementById('vault-search-bar');

        // Fachgebiet-Filter: nur ein Pfeil, und den gibt es erst bei offener
        // Suche (CSS). Das Menue baut `ladeFachgebiete`.
        this.fachgebiet = '';
        const fachgebietKnopf = document.getElementById('vault-fachgebiet-knopf');
        const fachgebietMenu = document.getElementById('vault-fachgebiet-menu');
        if (fachgebietKnopf && fachgebietMenu) {
            fachgebietKnopf.addEventListener('click', (e) => {
                e.stopPropagation();
                const offen = fachgebietMenu.classList.toggle('offen');
                fachgebietKnopf.setAttribute('aria-expanded', String(offen));
            });
            document.addEventListener('click', (e) => {
                if (!fachgebietMenu.contains(e.target) && e.target !== fachgebietKnopf) {
                    fachgebietMenu.classList.remove('offen');
                    fachgebietKnopf.setAttribute('aria-expanded', 'false');
                }
            });
            this.ladeFachgebiete();
        }

        const searchCloseBtn = document.getElementById('btn-vault-search-close');
        if (searchCloseBtn) {
            searchCloseBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.closeSearch();
            });
        }

        if (toggleSearchBtn && searchContainer && searchBar) {
            toggleSearchBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.openSearch();
            });

            // Close search when clicking outside (if empty)
            document.addEventListener('click', (e) => {
                if (!searchBar.classList.contains('visible')) return;
                if (this.searchInput && this.searchInput.value.trim()) return;
                if (searchContainer.contains(e.target)) return;
                this.closeSearch();
            });
        }

        // Search on Enter key
        if (this.searchInput) {
            this.searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.performSearch();
                }
            });

            // Live-Suche, aber nicht bei jedem Anschlag: die Suche laeuft ueber
            // die ganze Vault, und drei schnelle Tasten wuerden drei Laeufe
            // ausloesen, von denen zwei niemand mehr sieht.
            this.searchInput.addEventListener('input', () => {
                clearTimeout(this.sucheTimer);
                this.sucheTimer = setTimeout(() => this.performSearch(), 250);
            });

            // Close on Escape
            this.searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    this.closeSearch();
                }
            });
        }

        // Save button
        const saveBtn = document.getElementById('btn-vault-save');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => this.saveCurrentFile());
        }
    }

    /**
     * Open the Obsidian Vault panel at full width by default.
     * Uses the custom-width resize path so the editor can collapse to 0.
     */
    async open() {
        const editorPanel = document.querySelector('.editor-panel');

        this.panel.classList.add('visible', 'split-mode');
        this.panel.classList.remove('full-mode');
        this.sidebarEinklappen();

        if (editorPanel) {
            editorPanel.classList.add('split-with-vault');
        }

        // Expand to full container width by default. Respect a previously
        // saved custom width if the user has resized this panel before.
        const container = document.querySelector('.split-container');
        if (container) {
            const cs = getComputedStyle(container);
            const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
            const innerW = Math.max(0, container.clientWidth - padX);
            const gap = 20;
            const saved = localStorage.getItem(`panel-width-${this.panel.id}`);
            const savedW = saved ? parseInt(saved, 10) : NaN;
            const maxW = Math.max(0, innerW - gap);
            const minW = 280;
            // Clamp saved width to the current viewport so a width saved on
            // a wider window doesn't push the panel off-screen after resize.
            const targetW = Number.isFinite(savedW) && savedW > 0
                ? Math.max(minW, Math.min(savedW, maxW))
                : maxW;
            if (targetW > 0) {
                const widthPx = `${targetW}px`;
                this.panel.classList.add('custom-width');
                this.panel.style.width = widthPx;
                this.panel.style.maxWidth = widthPx;
                this.panel.style.flex = `0 0 ${widthPx}`;
            }
        }

        await this.loadFiles();
    }

    /**
     * Open the Obsidian Vault panel in split mode alongside the chat panel
     * Vault appears on the left, chat on the right
     */
    async openWithChat() {
        const chatPanel = document.getElementById('chat-interface');
        const editorPanel = document.querySelector('.editor-panel');

        // Show vault panel in split mode
        this.panel.classList.add('visible', 'split-mode');
        this.panel.classList.remove('full-mode');
        this.sidebarEinklappen();
        this.panel.style.display = 'flex';
        this.isFullscreen = false;

        // Ensure chat panel is in split mode
        if (chatPanel) {
            chatPanel.classList.add('visible', 'split-mode');
            chatPanel.classList.remove('full-mode');
            chatPanel.style.display = 'flex';
        }

        // Hide editor panel
        if (editorPanel) {
            editorPanel.style.display = 'none';
        }

        // Set split layout dimensions with order (vault left, chat right)
        const vaultStyle = {
            flex: '0 0 calc(50% - 10px)',
            maxWidth: 'calc(50% - 10px)',
            width: 'calc(50% - 10px)',
            order: '1'  // Vault first (left)
        };
        const chatStyle = {
            flex: '0 0 calc(50% - 10px)',
            maxWidth: 'calc(50% - 10px)',
            width: 'calc(50% - 10px)',
            order: '2'  // Chat second (right)
        };
        Object.assign(this.panel.style, vaultStyle);
        if (chatPanel) {
            Object.assign(chatPanel.style, chatStyle);
        }

        // Load files if not already loaded
        await this.loadFiles();
    }

    /**
     * Close the Obsidian Vault panel
     */
    close() {
        const chatPanel = document.getElementById('chat-interface');
        const editorPanel = document.querySelector('.editor-panel');

        // Hide vault panel and clear styles
        this.panel.classList.remove('visible', 'split-mode', 'full-mode');
        this.panel.style.display = '';
        this.panel.style.order = '';
        this.panel.style.flex = '';
        this.panel.style.width = '';
        this.panel.style.maxWidth = '';

        // Check if chat is actually visible
        const chatIsVisible = chatPanel && chatPanel.classList.contains('visible');

        if (chatIsVisible) {
            // Restore chat to split mode with editor
            chatPanel.classList.remove('full-mode');
            chatPanel.classList.add('split-mode');
            chatPanel.style.order = '';
            chatPanel.style.flex = '';
            chatPanel.style.width = '';
            chatPanel.style.maxWidth = '';

            // Show editor in split mode with chat
            if (editorPanel) {
                editorPanel.style.display = 'flex';
                editorPanel.classList.remove('split-with-vault');
                editorPanel.classList.add('split-with-chat');
            }
        } else {
            // No chat open - restore editor to full width
            if (editorPanel) {
                editorPanel.style.display = 'flex';
                editorPanel.classList.remove('split-with-chat', 'split-with-vault');
            }
        }
    }

    /**
     * Toggle sidebar visibility
     */
    /**
     * Vollbild: die Kachel nimmt die ganze Breite ein (`full-mode` in
     * layout.css blendet den Editor aus). Die runden Ecken bleiben — es ist
     * dieselbe Kachel, nur breiter.
     */
    toggleVollbild() {
        if (!this.panel) return;
        const vollbild = this.panel.classList.toggle('full-mode');
        const knopf = document.getElementById('btn-vault-fullscreen');
        if (knopf) {
            knopf.classList.toggle('is-vollbild', vollbild);
            knopf.title = vollbild ? 'Vollbild verlassen' : 'Vollbild';
        }
    }

    /// Beim Oeffnen steht die Dateiliste eingeklappt: die Notiz bekommt die
    /// ganze Breite, die Liste holt man sich mit dem Knopf dazu.
    sidebarEinklappen() {
        const sidebar = document.getElementById('vault-file-tree');
        if (sidebar) sidebar.classList.add('hidden');
        document.getElementById('btn-vault-toggle-sidebar')?.classList.add('is-collapsed');
    }

    toggleSidebar() {
        const sidebar = document.getElementById('vault-file-tree');
        if (!sidebar) return;
        const eingeklappt = sidebar.classList.toggle('hidden');
        // Das Icon zeigt denselben Balken wie der App-Switcher: breit, solange
        // die Liste steht, schmal im eingeklappten Zustand.
        document
            .getElementById('btn-vault-toggle-sidebar')
            ?.classList.toggle('is-collapsed', eingeklappt);
    }

    /**
     * Open search bar
     */
    openSearch() {
        const searchContainer = document.querySelector('.vault-search-container');
        const searchBar = document.getElementById('vault-search-bar');

        if (searchContainer && searchBar) {
            searchContainer.classList.add('search-active');
            searchBar.classList.add('visible');
            if (this.searchInput) {
                setTimeout(() => this.searchInput.focus(), 50);
            }
        }
    }

    /**
     * Close search bar
     */
    closeSearch() {
        const searchContainer = document.querySelector('.vault-search-container');
        const searchBar = document.getElementById('vault-search-bar');

        if (searchContainer && searchBar) {
            searchContainer.classList.remove('search-active');
            searchBar.classList.remove('visible');
            this.fachgebiet = '';
            const knopf = document.getElementById('vault-fachgebiet-knopf');
            if (knopf) {
                knopf.classList.remove('gefiltert');
                knopf.title = 'Nach Fachgebiet filtern';
            }
            document.querySelectorAll('.vault-fachgebiet-eintrag').forEach((el) => {
                el.classList.toggle('aktiv', el.dataset.tag === '');
            });
            if (this.searchInput) {
                this.searchInput.value = '';
                this.performSearch(); // Reset search results
            }
        }
    }

    /**
     * Toggle search bar visibility
     */
    toggleSearch() {
        const searchBar = document.getElementById('vault-search-bar');
        if (searchBar && searchBar.classList.contains('visible')) {
            this.closeSearch();
        } else {
            this.openSearch();
        }
    }

    /**
     * Aktive Vault + Liste laden und den Header beschriften.
     *
     * Blendet Pfeil und Ordner-Button standardmaessig aus (siehe index.html)
     * und schaltet sie nur bei Erfolg sichtbar — im Fehlerfall (z.B. 401 ohne
     * Anmeldung) muessen sie ausgeblendet bleiben, sonst saehe ein
     * Nicht-Admin Admin-Bedienelemente, die ins Leere klicken.
     */
    async loadVaultConfig() {
        const caret = this.vaultPicker?.querySelector('.vault-picker-caret');
        const browseBtn = document.getElementById('btn-vault-browse');
        const hideControls = () => {
            if (caret) caret.style.display = 'none';
            if (browseBtn) browseBtn.style.display = 'none';
        };

        try {
            const response = await fetch('/api/obsidian/vault/config');
            if (!response.ok) {
                console.error(`Vault-Konfiguration konnte nicht geladen werden: HTTP ${response.status}`);
                this.showStatusMessage('Vault-Konfiguration konnte nicht geladen werden', 4000);
                hideControls();
                return;
            }
            this.vaultConfig = await response.json();

            // Der Vault-Name stand hier frueher im Kopf der Kachel; seit dem
            // 17.09.2026 traegt ihn nur noch der Titel des Knopfes.
            if (this.vaultPicker) {
                this.vaultPicker.title = this.vaultConfig.active_path || this.vaultConfig.name || '';
            }

            // Ohne Auswahlrecht bleiben Pfeil und Ordner-Button verborgen
            if (caret) caret.style.display = this.vaultConfig.can_select ? '' : 'none';
            if (browseBtn) {
                browseBtn.style.display =
                    this.vaultConfig.can_select && this.vaultConfig.browse_available ? '' : 'none';
            }
        } catch (error) {
            console.error('Vault-Konfiguration konnte nicht geladen werden:', error);
            hideControls();
        }
    }

    toggleVaultMenu() {
        if (!this.vaultPickerMenu || !this.vaultConfig?.can_select) return;
        if (this.vaultPickerMenu.classList.contains('hidden')) {
            this.renderVaultPicker();
            this.vaultPickerMenu.classList.remove('hidden');
        } else {
            this.closeVaultMenu();
        }
    }

    closeVaultMenu() {
        if (this.vaultPickerMenu) this.vaultPickerMenu.classList.add('hidden');
    }

    renderVaultPicker() {
        if (!this.vaultPickerMenu || !this.vaultConfig) return;

        // Landet in `data-path="…"`, `data-forget="…"` und `title="…"` —
        // textContent/innerHTML allein escaped kein `"` (nur & < >), ein
        // POSIX-Pfad mit Anführungszeichen im Ordnernamen würde sonst aus
        // dem Attribut ausbrechen. Die Liste ist prozessweit: der
        // Ordnername eines Admins landet im Dropdown eines anderen.
        const escape = (str) => String(str || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const activePath = this.vaultConfig.active_path || '';
        const items = (this.vaultConfig.recent || []).map(entry => {
            const isActive = entry.path === activePath;
            return `
                <div class="vault-picker-item ${isActive ? 'is-active' : ''}" data-path="${escape(entry.path)}">
                    <span class="vault-picker-check">${isActive ? '✓' : ''}</span>
                    <span class="vault-picker-item-name" title="${escape(entry.path)}">${escape(entry.name)}</span>
                    <button class="vault-picker-forget" data-forget="${escape(entry.path)}" title="Aus der Liste entfernen">🗑</button>
                </div>
            `;
        }).join('');

        const browseRow = this.vaultConfig.browse_available
            ? `<div class="vault-picker-sep"></div>
               <div class="vault-picker-item vault-picker-browse" data-browse="1">Ordner wählen…</div>`
            : '';

        const empty = items ? '' : '<div class="vault-picker-empty">Noch keine Vault gewählt</div>';
        this.vaultPickerMenu.innerHTML = items + empty + browseRow;

        this.vaultPickerMenu.querySelectorAll('.vault-picker-forget').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.forgetVault(btn.dataset.forget);
            });
        });

        this.vaultPickerMenu.querySelectorAll('.vault-picker-item[data-path]').forEach(item => {
            item.addEventListener('click', () => this.selectVault(item.dataset.path));
        });

        const browseItem = this.vaultPickerMenu.querySelector('[data-browse]');
        if (browseItem) {
            browseItem.addEventListener('click', () => this.browseVault());
        }
    }

    /**
     * Vault wechseln und die Anzeige komplett neu aufbauen.
     */
    async selectVault(path) {
        this.closeVaultMenu();
        if (!path || path === this.vaultConfig?.active_path) return;

        try {
            const response = await fetch('/api/obsidian/vault/config', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path })
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                this.showStatusMessage(body.error || 'Vault konnte nicht gewechselt werden', 4000);
                return;
            }
            this.vaultConfig = await response.json();
            await this.switchVaultUi();
        } catch (error) {
            console.error('Vault-Wechsel fehlgeschlagen:', error);
            this.showStatusMessage('Vault konnte nicht gewechselt werden', 4000);
        }
    }

    async browseVault() {
        this.closeVaultMenu();
        try {
            const response = await fetch('/api/obsidian/vault/browse', { method: 'POST' });
            if (!response.ok) return;
            const result = await response.json();
            if (result.path) {
                await this.selectVault(result.path);
            }
        } catch (error) {
            console.error('Ordner-Dialog fehlgeschlagen:', error);
        }
    }

    async forgetVault(path) {
        try {
            const response = await fetch('/api/obsidian/vault/recent', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path })
            });
            if (!response.ok) return;
            const previousActive = this.vaultConfig?.active_path;
            this.vaultConfig = await response.json();
            if (previousActive === path) {
                // Die aktive Vault wurde vergessen — es gilt wieder der Config-Pfad
                await this.switchVaultUi();
            } else {
                this.renderVaultPicker();
            }
        } catch (error) {
            console.error('Vault konnte nicht aus der Liste entfernt werden:', error);
        }
    }

    /**
     * Nach einem Vault-Wechsel: alles verwerfen, was zur alten Vault gehoert.
     * Der gespeicherte Dateipfad in localStorage zeigt sonst ins Leere.
     */
    async switchVaultUi() {
        sessionStorage.removeItem('obsidian_last_file');
        this.files = [];
        this.currentFile = null;
        this.currentFilePath = null;
        if (this.currentFileSpan) this.currentFileSpan.textContent = '';
        this.previewContent.innerHTML = `
            <div class="vault-empty-state">
                <p>Datei auswählen</p>
            </div>
        `;
        await this.loadFiles();
    }

    /**
     * Load all files from the vault
     */
    async loadFiles() {
        try {
            await this.loadVaultConfig();
            this.showLoading(this.fileTreeContent);

            const response = await fetch('/api/obsidian/files');
            if (!response.ok) {
                throw new Error(`Failed to load files: ${response.statusText}`);
            }

            this.files = await response.json();
            this.renderFileTree();

            // Gemerkte Stelle der Sitzung, sonst der Radiologie-Index
            await this.autoOpenDefaultFile();

        } catch (error) {
            console.error('Error loading vault files:', error);
            this.showError(this.fileTreeContent, 'Fehler beim Laden der Dateien');
        }
    }

    /**
     * Beim Oeffnen zeigt die Vault den Radiologie-Index — es sei denn, in
     * dieser Browsersitzung war die Nutzerin schon woanders. Die Stelle steht
     * in sessionStorage: sie ueberlebt Neuladen und das Schliessen des
     * Panels, nicht aber das Schliessen des Browsers oder ein Abmelden.
     */
    async autoOpenDefaultFile() {
        // Vor dem 17.09.2026 lag die Stelle in localStorage und ueberlebte
        // jeden Neustart des Browsers — den Rest einmalig wegraeumen.
        try {
            localStorage.removeItem('obsidian_last_file');
        } catch {
            // ohne Speicher gibt es nichts zu raeumen
        }
        const gemerkt = sessionStorage.getItem('obsidian_last_file');
        const zuOeffnen = startdatei(this.files, gemerkt);
        if (!zuOeffnen) {
            sessionStorage.removeItem('obsidian_last_file');
            return;
        }
        await this.openFile(zuOeffnen);
    }

    /**
     * Render the file tree
     */
    renderFileTree() {
        if (this.files.length === 0) {
            this.fileTreeContent.innerHTML = `
                <div class="vault-empty-state">
                    <p>Keine Dateien gefunden</p>
                </div>
            `;
            return;
        }

        // Im Baum steht nur "Systeme Radiologie"; geladen ist die ganze Vault,
        // damit Verweise, Suche und Bilder weiter aufloesen.
        const tree = this.buildFileTree(sichtbareDateien(this.files));

        // Render the tree
        this.fileTreeContent.innerHTML = '';
        this.renderTreeNode(tree, this.fileTreeContent);
    }

    /**
     * Build a hierarchical file tree structure
     */
    buildFileTree(files) {
        const tree = {};

        // No filter needed - vault path is already configured on server
        // Skip files with empty path (root directory entry)
        const filteredFiles = files.filter(file => file.path && file.path.length > 0);

        filteredFiles.forEach(file => {
            const parts = file.path.split(/[/\\]/).filter(p => p.length > 0);
            let current = tree;

            parts.forEach((part, index) => {
                if (!current[part]) {
                    current[part] = {
                        name: part,
                        isFile: !file.is_dir && index === parts.length - 1,
                        path: file.path,
                        // Eigener Pfad des Knotens: ein Ordner trug bisher den
                        // Pfad der ersten Datei darin.
                        knotenPfad: parts.slice(0, index + 1).join('/'),
                        children: {}
                    };
                }
                current = current[part].children;
            });
        });

        return tree;
    }

    /**
     * Render a tree node (recursive)
     */
    renderTreeNode(node, container, level = 0) {
        Object.values(node).forEach(item => {
            if (!item.name) return;

            const itemDiv = document.createElement('div');
            itemDiv.className = `vault-tree-item ${item.isFile ? 'file' : 'folder'}`;
            itemDiv.style.paddingLeft = `${8 + level * 16}px`;
            itemDiv.dataset.pfad = item.knotenPfad || item.path || '';

            // Icon (only for folders - dropdown arrow)
            if (!item.isFile) {
                const icon = document.createElement('span');
                icon.className = 'vault-tree-item-icon folder-arrow';
                icon.innerHTML = '›';
                itemDiv.appendChild(icon);
            }

            // Name (remove file extension for files)
            const name = document.createElement('span');
            name.className = 'vault-tree-item-name';
            let displayName = item.name;
            if (item.isFile) {
                // Remove file extension (.md, .png, etc.)
                displayName = item.name.replace(/\.[^/.]+$/, '');
            }
            name.textContent = displayName;

            itemDiv.appendChild(name);

            container.appendChild(itemDiv);

            // Render children for folders
            if (!item.isFile && Object.keys(item.children).length > 0) {
                const childrenContainer = document.createElement('div');
                childrenContainer.className = 'vault-tree-folder-children';
                container.appendChild(childrenContainer);
                this.renderTreeNode(item.children, childrenContainer, level + 1);

                // Click handler for folders - toggle expansion
                itemDiv.addEventListener('click', (e) => {
                    e.stopPropagation();
                    itemDiv.classList.toggle('expanded');
                    childrenContainer.classList.toggle('collapsed');
                });

                // Start collapsed
                childrenContainer.classList.add('collapsed');
            } else if (item.isFile) {
                // Click handler for files
                itemDiv.addEventListener('click', () => {
                    // Check if it's an image file
                    if (/\.(png|jpg|jpeg|gif|svg|webp)$/i.test(item.path)) {
                        // Display image in preview pane
                        this.displayImage(item.path);
                    } else {
                        // Open markdown file in preview
                        this.openFile(item.path);
                    }
                });
            }
        });
    }

    /**
     * Display an image in the preview pane
     */
    displayImage(imagePath) {
        const previousFilePath = this.currentFilePath;

        // Extract filename without path and extension
        const fileName = imagePath.split(/[/\\]/).pop(); // Get last part after / or \
        const displayName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension

        // Display the image with error handling
        const imageUrl = `/api/obsidian/file/${encodeURIComponent(imagePath)}`;
        this.previewContent.innerHTML = `
            <div class="vault-image-content">
                <img src="${imageUrl}" alt="${displayName}"
                     style="max-width: 100%; height: auto; display: block; margin: 0 auto;"
                     onerror="this.parentElement.innerHTML='<div class=\\'vault-empty-state\\'><p>Bild konnte nicht geladen werden: ${displayName}</p></div>'">
            </div>
        `;

        // Only update current file path if image loads successfully
        const img = this.previewContent.querySelector('img');
        img.addEventListener('load', () => {
            this.currentFilePath = imagePath;
            this.currentFileSpan.textContent = displayName;
            this.highlightSelectedFile(imagePath);
        });

        img.addEventListener('error', () => {
            this.currentFilePath = previousFilePath;
        });
    }

    /**
     * Open a file and display its content
     */
    async openFile(filePath) {
        try {
            this.showLoading(this.previewContent);
            const previousFilePath = this.currentFilePath;
            const previousContent = this.currentFile;

            const response = await fetch(`/api/obsidian/file/${encodeURIComponent(filePath)}`);
            if (!response.ok) {
                // Don't change current file on error
                this.currentFilePath = previousFilePath;
                this.currentFile = previousContent;

                // Show temporary status message instead of replacing content
                const statusMsg = (response.status === 404 || response.status === 500)
                    ? 'Notiz existiert noch nicht.'
                    : `Fehler beim Laden: ${filePath.split(/[/\\]/).pop()}`;

                // Restore previous content if we had something loaded
                if (previousContent) {
                    // Re-render previous content
                    const { content: ohneMermaid, quellen } = mermaidAuslagern(ohneFrontmatter(previousContent));
                    let processedContent = this.convertObsidianSyntax(ohneMermaid, previousFilePath);
                    const { content: contentWithPlaceholders, mathBlocks } = this.extractMathBlocks(processedContent);
                    let html = vaultHtml(contentWithPlaceholders);
                    html = this.restoreMathBlocks(html, mathBlocks);
                    html = this.convertCallouts(html);
                    html = this.convertTags(html);
                    html = this.cleanupImageLineBreaks(html);
                    this.previewContent.innerHTML = `<div class="vault-markdown-content">${html}</div>`;
                    this.renderMath();
                    mermaidRendern(this.previewContent, quellen);
                    this.setupInternalLinkHandlers();
                    this.setupCalloutHandlers();
                } else {
                    // No previous content, show empty state
                    this.previewContent.innerHTML = `
                        <div class="vault-empty-state">
                            <p>Datei auswählen</p>
                        </div>
                    `;
                }

                // Show temporary status message
                this.showStatusMessage(statusMsg, 3000);
                return;
            }

            // Only update current file info after successful fetch
            this.currentFilePath = filePath;

            // Extract filename without path and extension
            const fileName = filePath.split(/[/\\]/).pop(); // Get last part after / or \
            const displayName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension
            this.currentFileSpan.textContent = displayName;

            const content = await response.text();
            this.currentFile = content;

            // Flussdiagramme vor allen Obsidian-Umwandlungen auslagern
            const { content: ohneMermaid, quellen } = mermaidAuslagern(ohneFrontmatter(content));

            // Convert Obsidian image syntax to standard markdown
            let processedContent = this.convertObsidianSyntax(ohneMermaid, filePath);

            // Extract and protect LaTeX blocks before markdown processing
            const { content: contentWithPlaceholders, mathBlocks } = this.extractMathBlocks(processedContent);

            // Render markdown
            let html = vaultHtml(contentWithPlaceholders);

            // Restore LaTeX blocks
            html = this.restoreMathBlocks(html, mathBlocks);

            // Convert Obsidian callouts to styled divs
            html = this.convertCallouts(html);

            // Convert tags to Obsidian-style format
            html = this.convertTags(html);

            // Remove line breaks around inline images (symbols, arrows)
            html = this.cleanupImageLineBreaks(html);

            this.previewContent.innerHTML = `<div class="vault-markdown-content">${html}</div>`;

            // Bullets mit Unterpunkten bekommen einen Pfeil zum Zuklappen
            listenFaltbarMachen(this.previewContent);

            // Render LaTeX math formulas if KaTeX is available
            this.renderMath();

            // Flussdiagramme zeichnen (asynchron, Mermaid lädt beim ersten Mal nach)
            mermaidRendern(this.previewContent, quellen);

            // Add click handlers for internal links
            this.setupInternalLinkHandlers();
            this.setupCalloutHandlers();

            // Highlight selected file in tree
            this.highlightSelectedFile(filePath);

            // Nur fuer diese Browsersitzung merken (s. autoOpenDefaultFile)
            sessionStorage.setItem('obsidian_last_file', filePath);

        } catch (error) {
            console.error('Error opening file:', error);
            this.showError(this.previewContent, 'Fehler beim Öffnen der Datei');
        }
    }

    /**
     * Convert Obsidian callouts to styled divs
     */
    convertCallouts(html) {
        // Match blockquotes that start with [!type] with optional +/- for folding
        // Obsidian callouts: > [!note], > [!warning], > [!info]+, > [!tip]-, etc.
        const calloutRegex = /<blockquote>\s*<p>\[!(\w+)\]([+-])?(?:\s+([^<]+?))?(?:<br>|\s*<\/p>)([\s\S]*?)<\/blockquote>/gi;

        return html.replace(calloutRegex, (match, type, foldIndicator, title, content) => {
            const calloutType = type.toLowerCase();
            // Clean up title - remove any trailing whitespace
            const calloutTitle = (title || type.charAt(0).toUpperCase() + type.slice(1)).trim();

            // Determine if callout is foldable and its initial state
            const isFoldable = foldIndicator === '+' || foldIndicator === '-';
            const isCollapsed = foldIndicator === '-';
            const collapsedClass = isCollapsed ? ' collapsed' : '';
            const foldIcon = isCollapsed ? '▶' : '▼';

            // No icons - keep it minimal
            const icon = '';
            const uniqueId = 'callout-' + Math.random().toString(36).substr(2, 9);

            if (isFoldable) {
                return `
                    <div class="obsidian-callout obsidian-callout-${calloutType} obsidian-callout-foldable${collapsedClass}" data-callout-id="${uniqueId}">
                        <div class="obsidian-callout-title" role="button" tabindex="0" data-callout-toggle="${uniqueId}">
                            <span class="obsidian-callout-fold">${foldIcon}</span>
                            <span class="obsidian-callout-icon">${icon}</span>
                            <span class="obsidian-callout-title-text">${calloutTitle}</span>
                        </div>
                        <div class="obsidian-callout-content">${content}</div>
                    </div>
                `;
            } else {
                return `
                    <div class="obsidian-callout obsidian-callout-${calloutType}">
                        <div class="obsidian-callout-title">
                            <span class="obsidian-callout-icon">${icon}</span>
                            <span class="obsidian-callout-title-text">${calloutTitle}</span>
                        </div>
                        <div class="obsidian-callout-content">${content}</div>
                    </div>
                `;
            }
        });
    }

    /**
     * Toggle collapsible callout visibility
     */
    toggleCallout(calloutId) {
        const calloutEl = document.querySelector(`[data-callout-id="${calloutId}"]`);
        if (!calloutEl) return;

        const isCollapsed = calloutEl.classList.contains('collapsed');
        const foldIcon = calloutEl.querySelector('.obsidian-callout-fold');

        if (isCollapsed) {
            calloutEl.classList.remove('collapsed');
            if (foldIcon) foldIcon.textContent = '▼';
        } else {
            calloutEl.classList.add('collapsed');
            if (foldIcon) foldIcon.textContent = '▶';
        }
    }

    /**
     * Convert tags to Obsidian-style format with # prefix
     */
    convertTags(html) {
        // Match tags in text content
        // Tags are words that start with # and contain letters, numbers, underscores, hyphens
        // Pattern: #word or #word_with_underscores or #word-with-hyphens
        const tagRegex = /#([a-zA-Z0-9_\u00C0-\u017F]+(?:[-_][a-zA-Z0-9_\u00C0-\u017F]+)*)/g;

        return html.replace(tagRegex, (match, tagName) => {
            // Return tag wrapped in span with class for styling
            return `<span class="obsidian-tag">#${tagName}</span>`;
        });
    }

    /**
     * Remove unnecessary line breaks around inline images (symbols, arrows in captions)
     */
    cleanupImageLineBreaks(html) {
        // Remove <br> directly before images
        html = html.replace(/<br\s*\/?>\s*(<img[^>]*>)/gi, '$1');
        // Remove <br> directly after images
        html = html.replace(/(<img[^>]*>)\s*<br\s*\/?>/gi, '$1');
        return html;
    }

    /**
     * Setup click handlers for internal links to prevent page navigation
     */
    setupInternalLinkHandlers() {
        const links = this.previewContent.querySelectorAll('a[href^="/api/obsidian/file/"]');
        links.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault(); // Prevent default navigation
                const href = link.getAttribute('href');
                // Extract file path from /api/obsidian/file/FILENAME
                const filePath = decodeURIComponent(href.replace('/api/obsidian/file/', ''));
                this.openFile(filePath);
            });
        });
    }

    /**
     * Klick auf einen faltbaren Callout.
     *
     * Frueher stand hier ein `onclick`-Attribut im erzeugten HTML. Die CSP der
     * Seite (`script-src 'self'`) verbietet Inline-Handler — in Produktion
     * liess sich seither KEIN zugeklappter Callout mehr oeffnen, die Konsole
     * meldete nur "Executing inline event handler violates ...".
     */
    setupCalloutHandlers() {
        this.previewContent.querySelectorAll('[data-callout-toggle]').forEach((titel) => {
            const id = titel.dataset.calloutToggle;
            titel.addEventListener('click', () => this.toggleCallout(id));
            titel.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.toggleCallout(id);
                }
            });
        });
    }

    /**
     * Convert Obsidian-specific syntax to standard Markdown
     */
    convertObsidianSyntax(content, currentFilePath) {
        // Obsidian-Markierungen: ==text== -> <mark>text</mark> (s. vault-notiz.js)
        content = markierungen(content);

        // Bilder auf die Vault-API, Breite aus `|500` (s. vault-bilder.js)
        content = bilderUmschreiben(content);

        // Convert Obsidian internal links (but not images): [[Note Name]] -> [Note Name](/api/obsidian/file/Note Name.md)
        // Skip if it's an image (already processed above)
        content = content.replace(/\[\[([^\]|]+?)(\|([^\]]+))?\]\]/g, (match, noteName, pipe, displayName) => {
            // Skip if it's an image extension (tolerate trailing whitespace
            // so `[[file.png |500]]` residues don't get `.md` appended).
            if (/\.(png|jpg|jpeg|gif|svg|webp)\s*$/i.test(noteName)) {
                return match;
            }
            noteName = noteName.trim();
            const display = displayName || noteName;
            const noteFile = noteName.endsWith('.md') ? noteName : noteName + '.md';
            return `[${display}](${encodeURI('/api/obsidian/file/' + noteFile)})`;
        });

        return content;
    }

    /**
     * Highlight the selected file in the tree
     */
    highlightSelectedFile(filePath) {
        if (!this.fileTreeContent) return;
        const gesucht = String(filePath || '').replace(/\\/g, '/');

        // Alles zuklappen: offen bleibt nur der Weg zur geoeffneten Notiz.
        this.fileTreeContent.querySelectorAll('.vault-tree-folder-children').forEach((kinder) => {
            kinder.classList.add('collapsed');
        });
        this.fileTreeContent.querySelectorAll('.vault-tree-item').forEach((eintrag) => {
            eintrag.classList.remove('expanded', 'selected');
        });

        const eintraege = [...this.fileTreeContent.querySelectorAll('.vault-tree-item.file')];
        // Ein Verweis in einer Notiz (`[[Aneurysmen (Index)]]`) nennt nur den
        // Dateinamen; aus dem Baum kommt der ganze Pfad. Beide Richtungen
        // muessen treffen, sonst bleibt der Baum beim Klick im Text stehen.
        const treffer =
            eintraege.find((e) => (e.dataset.pfad || '') === gesucht) ||
            eintraege.find((e) => (e.dataset.pfad || '').endsWith('/' + gesucht)) ||
            eintraege.find((e) => gesucht.endsWith('/' + (e.dataset.pfad || '')));
        if (!treffer) return;

        treffer.classList.add('selected');

        // Von der Notiz nach oben: jeder Ordner auf dem Weg geht auf. Der
        // Ordner-Eintrag steht im DOM direkt VOR seinem Kinder-Container.
        let kinder = treffer.parentElement;
        while (kinder && kinder !== this.fileTreeContent) {
            if (kinder.classList.contains('vault-tree-folder-children')) {
                kinder.classList.remove('collapsed');
                const ordner = kinder.previousElementSibling;
                if (ordner && ordner.classList.contains('vault-tree-item')) {
                    ordner.classList.add('expanded');
                }
            }
            kinder = kinder.parentElement;
        }

        treffer.scrollIntoView({ block: 'nearest' });
    }

    /**
     * Extract math blocks before markdown processing to protect them
     */
    extractMathBlocks(content) {
        const mathBlocks = [];
        let index = 0;

        // Extract display math ($$...$$) - multiline support
        content = content.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
            const placeholder = `MATH_BLOCK_${index}`;
            mathBlocks[index] = { type: 'display', formula: formula.trim() };
            index++;
            return placeholder;
        });

        // Extract inline math ($...$) - single line
        content = content.replace(/\$([^\n$]+?)\$/g, (match, formula) => {
            const placeholder = `MATH_INLINE_${index}`;
            mathBlocks[index] = { type: 'inline', formula: formula.trim() };
            index++;
            return placeholder;
        });

        return { content, mathBlocks };
    }

    /**
     * Restore math blocks after markdown processing
     */
    restoreMathBlocks(html, mathBlocks) {
        mathBlocks.forEach((block, index) => {
            if (block.type === 'display') {
                const placeholder = `MATH_BLOCK_${index}`;
                html = html.replace(placeholder, `<span class="math-display">$$${block.formula}$$</span>`);
            } else {
                const placeholder = `MATH_INLINE_${index}`;
                html = html.replace(placeholder, `<span class="math-inline">$${block.formula}$</span>`);
            }
        });
        return html;
    }

    /**
     * Render LaTeX math formulas using KaTeX
     */
    renderMath() {
        // Check if KaTeX is loaded
        if (typeof renderMathInElement !== 'undefined') {
            const mathContent = this.previewContent.querySelector('.vault-markdown-content');
            if (mathContent) {
                try {
                    renderMathInElement(mathContent, {
                        delimiters: [
                            {left: '$$', right: '$$', display: true},   // Display math
                            {left: '$', right: '$', display: false},    // Inline math
                        ],
                        throwOnError: false,
                        errorColor: '#cc0000',
                        strict: false
                    });
                } catch (error) {
                    console.error('Error rendering math:', error);
                }
            }
        }
    }

    /**
     * Save the current file
     */
    async saveCurrentFile() {
        if (!this.currentFilePath) {
            alert('Keine Datei geöffnet');
            return;
        }

        try {
            const response = await fetch(`/api/obsidian/file/${encodeURIComponent(this.currentFilePath)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    content: this.currentFile
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to save file: ${response.statusText}`);
            }

            await response.json();
            alert('Datei gespeichert!');

        } catch (error) {
            console.error('Error saving file:', error);
            alert('Fehler beim Speichern der Datei');
        }
    }

    /**
     * Fuellt das Fachgebiet-Dropdown aus den Tags der Vault.
     */
    async ladeFachgebiete() {
        const menu = document.getElementById('vault-fachgebiet-menu');
        const knopf = document.getElementById('vault-fachgebiet-knopf');
        if (!menu || !knopf) return;

        let gebiete = [];
        try {
            const antwort = await fetch('/api/obsidian/fachgebiete');
            if (antwort.ok) {
                gebiete = (await antwort.json()).fachgebiete || [];
            }
        } catch (fehler) {
            // Ohne Liste bleibt es bei „Alle Fachgebiete".
        }

        const eintragBauen = (tag, name) => {
            const knopfchen = document.createElement('button');
            knopfchen.type = 'button';
            knopfchen.className = 'vault-fachgebiet-eintrag';
            knopfchen.setAttribute('role', 'menuitemradio');
            knopfchen.dataset.tag = tag;
            knopfchen.textContent = name;
            knopfchen.addEventListener('click', (e) => {
                e.stopPropagation();
                this.fachgebiet = tag;
                menu.querySelectorAll('.vault-fachgebiet-eintrag').forEach((el) => {
                    el.classList.toggle('aktiv', el.dataset.tag === tag);
                    el.setAttribute('aria-checked', String(el.dataset.tag === tag));
                });
                // Der Pfeil zeigt an, dass gefiltert wird.
                knopf.classList.toggle('gefiltert', Boolean(tag));
                knopf.title = tag ? `Fachgebiet: ${name}` : 'Nach Fachgebiet filtern';
                menu.classList.remove('offen');
                knopf.setAttribute('aria-expanded', 'false');
                this.performSearch();
            });
            return knopfchen;
        };

        menu.innerHTML = '';
        const alle = eintragBauen('', 'Alle Fachgebiete');
        alle.classList.add('aktiv');
        alle.setAttribute('aria-checked', 'true');
        menu.appendChild(alle);
        for (const gebiet of gebiete) {
            menu.appendChild(eintragBauen(gebiet.tag, gebiet.name));
        }
    }

    /**
     * Perform a search in the vault
     */
    async performSearch() {
        const query = this.searchInput.value.trim();
        const fachgebiet = this.fachgebiet || '';
        // Ohne beides gibt es nichts zu suchen — dann steht wieder der Baum da.
        if (!query && !fachgebiet) {
            this.renderFileTree();
            return;
        }

        try {
            this.showLoading(this.fileTreeContent);

            const response = await fetch('/api/obsidian/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: query,
                    max_results: 50,
                    fachgebiet: fachgebiet || null
                })
            });

            if (!response.ok) {
                throw new Error(`Search failed: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Search response:', data);
            // Handle both { results: [...] } and direct array response
            const results = Array.isArray(data) ? data : (data.results || []);
            this.renderSearchResults(results, query);

        } catch (error) {
            console.error('Search error:', error);
            this.showError(this.fileTreeContent, 'Fehler bei der Suche');
        }
    }

    /**
     * Render search results
     */
    renderSearchResults(results, query) {
        // Handle null/undefined results
        if (!results || !Array.isArray(results)) {
            this.fileTreeContent.innerHTML = `
                <div class="vault-empty-state">
                    <p>Keine Ergebnisse für "${query}"</p>
                </div>
            `;
            return;
        }

        // Filter results to only show "Systeme Radiologie" folder
        const filteredResults = results.filter(result => {
            const filePath = result.file_path || result.file || '';
            const normalizedPath = filePath.replace(/\\/g, '/');
            return normalizedPath.startsWith('Systeme Radiologie/');
        });

        if (filteredResults.length === 0) {
            this.fileTreeContent.innerHTML = `
                <div class="vault-empty-state">
                    <p>Keine Ergebnisse für "${query}"</p>
                </div>
            `;
            return;
        }

        this.fileTreeContent.innerHTML = '<div class="vault-search-results"></div>';
        const container = this.fileTreeContent.querySelector('.vault-search-results');

        filteredResults.forEach(result => {
            const resultDiv = document.createElement('div');
            resultDiv.className = 'vault-search-result-item';

            const fileDiv = document.createElement('div');
            fileDiv.className = 'vault-search-result-file';
            // Extract filename without path and extension
            const fullPath = result.file_path || result.file;
            const fileName = fullPath.split(/[/\\]/).pop();
            const displayName = fileName.replace(/\.[^/.]+$/, '');
            fileDiv.textContent = displayName;

            const contextDiv = document.createElement('div');
            contextDiv.className = 'vault-search-result-context';

            // Highlight the match
            const contextText = result.line_content || result.context || result.line || '';
            const highlightedText = this.highlightSearchMatch(contextText, query);
            contextDiv.innerHTML = highlightedText;

            resultDiv.appendChild(fileDiv);
            resultDiv.appendChild(contextDiv);

            // Click to open file
            resultDiv.addEventListener('click', () => {
                this.openFile(result.file_path || result.file);
                // Restore file tree view
                this.searchInput.value = '';
                this.renderFileTree();
            });

            container.appendChild(resultDiv);
        });
    }

    /**
     * Highlight search match in text
     */
    highlightSearchMatch(text, query) {
        if (!text) return '';
        const regex = new RegExp(`(${query})`, 'gi');
        return text.replace(regex, '<span class="vault-search-result-match">$1</span>');
    }

    /**
     * Show loading state
     */
    showLoading(container) {
        container.innerHTML = `
            <div class="vault-loading">
                <p>Lädt...</p>
            </div>
        `;
    }

    /**
     * Show error message
     */
    showError(container, message) {
        container.innerHTML = `
            <div class="vault-empty-state">
                <p style="color: #f87171;">${message}</p>
            </div>
        `;
    }

    /**
     * Show a temporary status message that auto-hides after a few seconds
     * @param {string} message - The message to display
     * @param {number} duration - Duration in ms before auto-hide (default 3000)
     */
    showStatusMessage(message, duration = 3000) {
        const statusStrip = document.getElementById('vault-status-strip');
        const statusMessage = document.getElementById('vault-status-message');

        if (!statusStrip || !statusMessage) return;

        // Clear any existing timeout
        if (this.statusTimeout) {
            clearTimeout(this.statusTimeout);
        }

        // Remove fade-out class if present
        statusStrip.classList.remove('fade-out');

        // Set message and show
        statusMessage.textContent = message;
        statusStrip.style.display = 'block';

        // Auto-hide after duration
        this.statusTimeout = setTimeout(() => {
            statusStrip.classList.add('fade-out');
            setTimeout(() => {
                statusStrip.style.display = 'none';
                statusStrip.classList.remove('fade-out');
            }, 300); // Match animation duration
        }, duration);
    }
}

// Export singleton instance
export const obsidianVault = new ObsidianVaultManager();

// Make it globally accessible for inline event handlers
window.obsidianVaultManager = obsidianVault;
